/**
 * RecoverPage — self-service passkey reset request (/recover).
 * Posts the email to /api/auth/reset/request; the response is always ok
 * (no enumeration), so the confirmation copy is unconditional.
 */

import { type FormEvent, useCallback, useState } from "react";
import { API_URL } from "@/lib/env";

const PANEL_CLASS =
  "rounded-[32px] border border-[rgba(10,52,96,0.10)] bg-white/92 p-8 shadow-[0_24px_64px_rgba(10,52,96,0.10)] backdrop-blur-xl sm:p-10";
const FIELD_CLASS =
  "mt-2 w-full rounded-[18px] border border-[rgba(12,63,107,0.18)] bg-white px-4 py-3.5 text-[15px] text-[#111111] shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] outline-none transition-all duration-200 placeholder:text-[rgba(125,156,180,0.95)] focus:border-[rgba(12,63,107,0.46)] focus:ring-4 focus:ring-[rgba(238,244,248,0.9)]";
const PRIMARY_BUTTON_CLASS =
  "w-full rounded-full bg-[var(--cs-title-navy)] px-5 py-3.5 text-[15px] font-semibold text-white shadow-[0_16px_34px_rgba(10,52,96,0.18)] transition-colors duration-200 hover:bg-[var(--cs-title-navy)] disabled:cursor-not-allowed disabled:opacity-50";

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className={PANEL_CLASS}>
      <div className="mb-8 text-center">
        <h1 className="text-[34px] font-semibold tracking-[-0.04em] text-[var(--cs-title-navy)]">{title}</h1>
      </div>
      {children}
    </div>
  );
}

export function RecoverPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRequest = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError("");
      setLoading(true);
      try {
        const resp = await fetch(`${API_URL}/api/auth/reset/request`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ email: email.trim() }),
        });
        if (!resp.ok) {
          const body = (await resp.json().catch(() => ({}))) as { detail?: string };
          throw new Error(body.detail ?? `Fout ${resp.status}`);
        }
        setSent(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Versturen mislukt");
      } finally {
        setLoading(false);
      }
    },
    [email],
  );

  return (
    <div className="cs-marketing min-h-screen bg-[linear-gradient(180deg,#fcfbf7_0%,#ffffff_22%,#f3f9fd_100%)] px-4 py-12 sm:px-6 sm:py-16">
      <div className="mx-auto w-full max-w-md space-y-6">
        <Card title="Passkey opnieuw instellen">
          {sent ? (
            <>
              <p className="mb-6 text-center text-[15px] text-[rgba(95,131,160,0.96)]">
                Als dit e-mailadres bij ons bekend is, is er een e-mail verstuurd. Controleer ook
                uw spam-map.
              </p>
              <div className="text-center text-sm text-[rgba(95,131,160,0.96)]">
                <a
                  href="/login"
                  className="text-[var(--cs-title-navy)] transition-colors hover:text-[var(--cs-title-navy)]"
                >
                  ← Terug naar inloggen
                </a>
              </div>
            </>
          ) : (
            <>
              <p className="mb-5 text-center text-[15px] text-[rgba(95,131,160,0.96)]">
                Voer uw e-mailadres in. Als dit adres bij ons bekend is, sturen we een e-mail met
                een link (15 minuten geldig) om een nieuwe passkey in te stellen. Uw oude passkeys
                blijven werken tot de nieuwe is ingesteld.
              </p>
              {error ? (
                <div className="mb-5 rounded-[18px] border border-[#F3C7CD] bg-[#FFF4F6] p-4 text-sm text-[#B42318]">
                  {error}
                </div>
              ) : null}
              <form onSubmit={handleRequest} className="space-y-5">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-[15px] font-medium text-[var(--cs-title-navy)]"
                  >
                    E-mailadres
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                    className={FIELD_CLASS}
                  />
                </div>
                <button type="submit" disabled={loading} className={PRIMARY_BUTTON_CLASS}>
                  {loading ? "Versturen…" : "Verstuur e-mail"}
                </button>
              </form>
              <div className="mt-6 text-center text-sm text-[rgba(95,131,160,0.96)]">
                <a
                  href="/login"
                  className="text-[var(--cs-title-navy)] transition-colors hover:text-[var(--cs-title-navy)]"
                >
                  ← Terug naar inloggen
                </a>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
